import { UserRelationshipType } from '@timesheet/schemas';
import { MonthCalendar, PendingApproval, TimesheetEntry, TimesheetIssue } from '../../services/timesheetService';
import { isOnCall } from '../../lib/timesheet-hours';

/** Tab order on the approvals desk: one tab per relationship type. */
export const RELATIONSHIP_TABS: UserRelationshipType[] = [
  'INTERNAL_EMPLOYEE',
  'FREELANCER',
  'SUBCONTRACTOR',
  'OTHER',
];

export const isRelationshipType = (value: unknown): value is UserRelationshipType =>
  typeof value === 'string' && (RELATIONSHIP_TABS as string[]).includes(value);

export type DayState = 'none' | 'pending' | 'approved' | 'mixed';

export interface DayCell {
  /** 1-based day of month */
  day: number;
  /** yyyy-MM-dd */
  dateKey: string;
  workedHours: number;
  overtimeHours: number;
  onCallHours: number;
  /** On call that day, even with 0 hours of work */
  onCall: boolean;
  permitHours: number;
  entries: number;
  state: DayState;
  isWeekend: boolean;
}

export interface PersonMonthRow {
  userId: number;
  name: string;
  relationshipType: UserRelationshipType;
  entries: TimesheetEntry[];
  /** ids of entries still waiting for a decision */
  pendingIds: number[];
  approvedCount: number;
  workedHours: number;
  overtimeHours: number;
  onCallHours: number;
  permitHours: number;
  /** Consistency checks computed by the backend, errors first; feeds the "Problemi" column. */
  issues: TimesheetIssue[];
  stats: PendingApproval['stats'];
}

export interface RowTotals {
  people: number;
  pendingCount: number;
  /** people with at least one issue */
  issuesCount: number;
  workedHours: number;
  overtimeHours: number;
  onCallHours: number;
}

const num = (value: unknown): number => Number(value) || 0;

const localDateKey = (date: Date): string => {
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${mm}-${dd}`;
};

/**
 * Calendar key (yyyy-MM-dd). Date-only strings are taken as they are; the API also serialises
 * date columns as UTC timestamps (local midnight shifted by the server offset), which — like the
 * rest of the app — are read back in the browser's local time.
 */
export const toDateKey = (date: string | Date): string => {
  if (date instanceof Date) return localDateKey(date);
  const value = String(date);
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value.slice(0, 10) : localDateKey(parsed);
};

export const monthKey = (year: number, month: number): string =>
  `${year}-${String(month).padStart(2, '0')}`;

export const parseMonthKey = (value: string | null | undefined): { year: number; month: number } | null => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return { year, month };
};

export function buildRows(
  groups: PendingApproval[],
  resolveType: (userId: number) => UserRelationshipType | undefined,
): PersonMonthRow[] {
  return groups.map((group) => {
    let workedHours = 0;
    let overtimeHours = 0;
    let onCallHours = 0;
    let permitHours = 0;
    let approvedCount = 0;
    const pendingIds: number[] = [];

    for (const entry of group.entries) {
      workedHours += num(entry.workedHours);
      overtimeHours += num(entry.overtimeHours);
      onCallHours += num(entry.onCallHours);
      permitHours += num(entry.workPermit);
      if (entry.state === 'in_approval' && entry.id != null) pendingIds.push(entry.id);
      if (entry.state === 'approved') approvedCount += 1;
    }

    return {
      userId: group.user.id,
      name: group.user.name,
      relationshipType: group.user.relationshipType ?? resolveType(group.user.id) ?? 'OTHER',
      entries: group.entries,
      pendingIds,
      approvedCount,
      workedHours,
      overtimeHours,
      onCallHours,
      permitHours,
      issues: group.issues ?? [],
      stats: group.stats,
    };
  });
}

/** Every calendar day touched by at least one issue. */
export function issueDayKeys(issues: TimesheetIssue[]): Set<string> {
  const keys = new Set<string>();
  issues.forEach((issue) => issue.dates.forEach((date) => keys.add(date)));
  return keys;
}

/** Issues per calendar day, keeping the errors-first order of the input. */
export function issuesByDay(issues: TimesheetIssue[]): Map<string, TimesheetIssue[]> {
  const map = new Map<string, TimesheetIssue[]>();
  issues.forEach((issue) =>
    issue.dates.forEach((date) => {
      if (!map.has(date)) map.set(date, []);
      map.get(date)!.push(issue);
    }),
  );
  return map;
}

export type OffDayKind = 'holiday' | 'closing';

/** Weekday off days of the month by calendar key: national holidays and company closings. */
export function offDaysByKey(calendar: MonthCalendar | null | undefined): Map<string, OffDayKind> {
  const map = new Map<string, OffDayKind>();
  calendar?.closingDates.forEach((key) => map.set(key, 'closing'));
  calendar?.holidayDates.forEach((key) => map.set(key, 'holiday'));
  return map;
}

export function groupRowsByType(rows: PersonMonthRow[]): Record<UserRelationshipType, PersonMonthRow[]> {
  const grouped = {
    INTERNAL_EMPLOYEE: [],
    FREELANCER: [],
    SUBCONTRACTOR: [],
    OTHER: [],
  } as Record<UserRelationshipType, PersonMonthRow[]>;
  for (const row of rows) {
    grouped[row.relationshipType].push(row);
  }
  return grouped;
}

export function sumRows(rows: PersonMonthRow[]): RowTotals {
  return rows.reduce<RowTotals>(
    (acc, row) => ({
      people: acc.people + 1,
      pendingCount: acc.pendingCount + row.pendingIds.length,
      issuesCount: acc.issuesCount + (row.issues.length > 0 ? 1 : 0),
      workedHours: acc.workedHours + row.workedHours,
      overtimeHours: acc.overtimeHours + row.overtimeHours,
      onCallHours: acc.onCallHours + row.onCallHours,
    }),
    { people: 0, pendingCount: 0, issuesCount: 0, workedHours: 0, overtimeHours: 0, onCallHours: 0 },
  );
}

/** One cell per calendar day of the month, folding multiple entries (orders) of the same day together. */
export function buildMonthCells(entries: TimesheetEntry[], year: number, month: number): DayCell[] {
  const prefix = monthKey(year, month);
  const daysInMonth = new Date(year, month, 0).getDate();
  const byDay = new Map<number, TimesheetEntry[]>();

  for (const entry of entries) {
    const key = toDateKey(entry.date);
    if (!key.startsWith(prefix)) continue;
    const day = Number(key.slice(8, 10));
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day)!.push(entry);
  }

  return Array.from({ length: daysInMonth }, (_, index) => {
    const day = index + 1;
    const dayEntries = byDay.get(day) ?? [];
    const pending = dayEntries.some((e) => e.state === 'in_approval');
    const approved = dayEntries.some((e) => e.state === 'approved');
    const weekday = new Date(year, month - 1, day).getDay();

    return {
      day,
      dateKey: `${prefix}-${String(day).padStart(2, '0')}`,
      workedHours: dayEntries.reduce((sum, e) => sum + num(e.workedHours), 0),
      overtimeHours: dayEntries.reduce((sum, e) => sum + num(e.overtimeHours), 0),
      onCallHours: dayEntries.reduce((sum, e) => sum + num(e.onCallHours), 0),
      onCall: dayEntries.some(isOnCall),
      permitHours: dayEntries.reduce((sum, e) => sum + num(e.workPermit), 0),
      entries: dayEntries.length,
      state: pending && approved ? 'mixed' : pending ? 'pending' : approved ? 'approved' : 'none',
      isWeekend: weekday === 0 || weekday === 6,
    };
  });
}

/**
 * Ledger-style hours with locale separators: at least one decimal, two when the minutes need them
 * ("168,0", "1,5", "1,33" for 1:20 in it; "168.0" in en). Two is what the timesheet stores.
 */
export function formatHours(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 2 }).format(value);
}
